
import { UI_ICONS } from "@/lib/ui-icons";
import { useState, useEffect, useContext, useCallback, useRef, createContext, type ReactNode } from "react";
import { useRouter } from "@/lib/navigation";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import { alpha, useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Iconify } from "@/lib/iconify";
import { ToastContext } from "@/lib/toast";
import { createProject, getProjectCounts, listProjects } from "@/blyn/services/projectsService";
import { store } from "@/blyn/legacy/state/store";
import { listKnowledgeSources, getBillingOverview, type KnowledgeSourceSummary as ApiKnowledgeSourceSummary } from "@/blyn/services/blynApiClient";
import { getTenantId as resolveTenantId } from "@/blyn/services/currentUser";

type KnowledgeSourceSummary = ApiKnowledgeSourceSummary & { name?: string; description?: string };

function getTenantId(): string {
  return resolveTenantId();
}

// ─── Context ─────────────────────────────────────────────────────────────────

const NewProjectContext = createContext<() => void>(() => {});

export function useNewProject(): () => void {
  return useContext(NewProjectContext);
}

// Dispatches a DOM event so legacy vanilla-JS callers (main.ts, palette.ts) can
// still trigger the dialog without importing React context.
export function openNewProject() {
  window.dispatchEvent(new CustomEvent("blyn:open-new-project"));
}

// ─── Types ──────────────────────────────────────────────────────────────────────

type WizardState = {
  step: 1 | 2 | 3;
  name: string;
  goal: string;
  customGoal: string;
  audience: string;
  budget: string;
  date: Date | null;
};

const FRESH: WizardState = {
  step: 1, name: "", goal: "winback", customGoal: "", audience: "dormant_90d", budget: "", date: null,
};

// Step 2's recommended audience follows the Step 1 goal — e.g. a winback
// goal defaults to the dormant-90d segment, cross-sell/retain point at the
// existing top-LTV cohort, and acquisition (no existing customers to target)
// falls back to a custom audience.
const GOAL_AUDIENCE_DEFAULTS: Record<string, string> = {
  winback: "dormant_90d",
  crosssell: "high_ltv",
  retain: "high_ltv",
  acquire: "custom",
  custom: "custom",
};

// ─── Shared sub-components ────────────────────────────────────────────────────

function RadioCard({ selected, title, sub, onClick }: { selected: boolean; title: string; sub: string; onClick: () => void }) {
  const theme = useTheme();
  return (
    <Stack direction="row" gap={1.25} alignItems="flex-start" onClick={onClick}
      sx={{
        p: "10px 14px", borderRadius: "10px", border: "1px solid", cursor: "pointer",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : "transparent",
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      <Box sx={{
        width: 16, height: 16, borderRadius: "50%", border: "2px solid", mt: 0.25, flexShrink: 0,
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? "primary.main" : "transparent",
        display: "grid", placeItems: "center",
      }}>
        {selected && <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "common.white" }} />}
      </Box>
      <Box>
        <Typography variant="body2" fontWeight={selected ? 600 : 500} sx={{ textTransform: "capitalize" }}>{title}</Typography>
        <Typography variant="caption" sx={{ color: "var(--text-tertiary)" }}>{sub}</Typography>
      </Box>
    </Stack>
  );
}

function CheckCard({ checked, title, sub, onChange }: { checked: boolean; title: string; sub: string; onChange: () => void }) {
  const theme = useTheme();
  return (
    <Stack direction="row" gap={1.25} alignItems="flex-start" onClick={onChange}
      sx={{
        p: "10px 14px", borderRadius: "10px", border: "1px solid", cursor: "pointer",
        borderColor: checked ? "primary.main" : "divider",
        bgcolor: checked ? alpha(theme.palette.primary.main, 0.06) : "transparent",
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      <Checkbox size="small" checked={checked} disableRipple sx={{ p: 0, mt: 0.1, "& .MuiSvgIcon-root": { fontSize: 17 } }} />
      <Box>
        <Typography variant="body2" fontWeight={checked ? 600 : 500}>{title}</Typography>
        <Typography variant="caption" sx={{ color: "var(--text-tertiary)" }}>{sub}</Typography>
      </Box>
    </Stack>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 0.75, letterSpacing: "0.02em", textTransform: "capitalize" }}>
      {children}
    </Typography>
  );
}

// ─── Steps ────────────────────────────────────────────────────────────────────

function Step1({ state, update, nameError }: { state: WizardState; update: (k: keyof WizardState, v: string) => void; nameError: boolean }) {
  const GOALS = [
    { id: "winback",   title: "Reactivate dormant customers",    sub: "Winback campaign targeting inactive accounts" },
    { id: "acquire",   title: "Acquire new customers",           sub: "Prospecting via lookalike audiences" },
    { id: "crosssell", title: "Cross-sell to existing customers", sub: "Upgrade or expand product holdings" },
    { id: "retain",    title: "Retain at-risk customers",        sub: "Churn prevention via targeted touches" },
    { id: "custom",    title: "Custom objective",                sub: "Describe your own campaign goal" },
  ];
  return (
    <Stack gap={2.5}>
      <TextField
        fullWidth size="small" autoFocus label="Project Name *"
        sx={{ mt: 1.5 }}
        value={state.name}
        onChange={(e) => update("name", e.target.value)}
        error={nameError}
        helperText={nameError ? "Project name is required" : ""}
      />
      <Box>
        <FieldLabel>What's the goal?</FieldLabel>
        <Stack gap={0.75}>
          {GOALS.map((g) => (
            <RadioCard key={g.id} selected={state.goal === g.id} title={g.title} sub={g.sub} onClick={() => update("goal", g.id)} />
          ))}
        </Stack>
        {state.goal === "custom" && (
          <TextField
            fullWidth size="small" label="Describe Your Objective" multiline minRows={2} sx={{ mt: 1 }}
            value={state.customGoal}
            onChange={(e) => update("customGoal", e.target.value)}
            placeholder="e.g. Re-engage lapsed subscribers ahead of a seasonal promotion"
          />
        )}
      </Box>
    </Stack>
  );
}

function Step2({ state, update, updateDate }: { state: WizardState; update: (k: keyof WizardState, v: string) => void; updateDate: (v: Date | null) => void }) {
  const AUDIENCES = [
    { id: "dormant_90d", title: "Dormant 90+ days",     sub: "Recently inactive customers" },
    { id: "high_ltv",    title: "Top LTV cohort",        sub: "Highest-value customers" },
    { id: "custom",      title: "Build custom audience", sub: "Define Criteria In Audience Builder After Creation" },
  ];
  const recommendedId = GOAL_AUDIENCE_DEFAULTS[state.goal];
  return (
    <Stack gap={2.5}>
      <Box>
        <FieldLabel>Target audience</FieldLabel>
        <Stack gap={0.75}>
          {AUDIENCES.map((a) => (
            <RadioCard
              key={a.id}
              selected={state.audience === a.id}
              title={a.title}
              sub={a.id === recommendedId ? `${a.sub} · Recommended for this goal` : a.sub}
              onClick={() => update("audience", a.id)}
            />
          ))}
        </Stack>
      </Box>
      <Stack direction="row" gap={1.5}>
        <TextField sx={{ flex: 1 }} size="small" label="Budget Cap" value={state.budget} onChange={(e) => update("budget", e.target.value)} />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Target Launch Date"
            value={state.date}
            onChange={updateDate}
            slotProps={{ textField: { size: "small", sx: { flex: 1 } } }}
          />
        </LocalizationProvider>
      </Stack>
    </Stack>
  );
}

function Step3({
  checks,
  toggle,
  onSourcesLoaded,
  planInfo,
}: {
  checks: Record<string, boolean>;
  toggle: (k: string) => void;
  onSourcesLoaded: (sources: KnowledgeSourceSummary[]) => void;
  planInfo: { projectCount: number; planName: string | null } | null;
}) {
  const [sources, setSources] = useState<KnowledgeSourceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    listKnowledgeSources(getTenantId(), undefined, "tenant")
      .then((res) => {
        if (cancelled) return;
        setSources(res.items);
        onSourcesLoaded(res.items);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack gap={2.5}>
      <Box>
        <FieldLabel>Connect knowledge base sources</FieldLabel>
        <Stack gap={0.75}>
          {loading ? (
            <Typography variant="caption" sx={{ color: "var(--text-tertiary)" }}>Loading knowledge sources…</Typography>
          ) : error ? (
            <Typography variant="caption" sx={{ color: "var(--text-tertiary)" }}>Couldn't load knowledge sources.</Typography>
          ) : sources.length === 0 ? (
            <Typography variant="caption" sx={{ color: "var(--text-tertiary)" }}>No tenant knowledge sources yet.</Typography>
          ) : sources.map((s) => (
            <CheckCard
              key={s.kb_id}
              checked={!!checks[s.kb_id]}
              title={String(s.name ?? s.kb_id)}
              sub={`Tenant scope${s.description ? ` · ${String(s.description).split(". ")[0]}` : ""}`}
              onChange={() => toggle(s.kb_id)}
            />
          ))}
        </Stack>
      </Box>
      <Box>
        <TextField fullWidth size="small" label="Invite Team Members (Optional)" placeholder="email@boostt.ai · comma-separated" />
        <Typography variant="caption" sx={{ color: "var(--text-tertiary)", mt: 0.75, display: "block" }}>
          You can invite people later from project settings.
        </Typography>
      </Box>
      {planInfo && (
        <Stack direction="row" gap={1} alignItems="flex-start"
          sx={{ p: "10px 12px", borderRadius: "10px", border: "1px solid", borderColor: "divider", bgcolor: "action.hover" }}
        >
          <Iconify icon={UI_ICONS.info} width={14} color="text.secondary" sx={{ mt: 0.1, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.55 }}>
            This will bring you to {planInfo.projectCount + 1} project{planInfo.projectCount + 1 === 1 ? "" : "s"}
            {planInfo.planName ? ` on the ${planInfo.planName} plan.` : "."}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function NewProjectDialogProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const appToast = useContext(ToastContext);

  const [open, setOpen] = useState(false);
  const [state, setState] = useState<WizardState>(FRESH);
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [creating, setCreating] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [planInfo, setPlanInfo] = useState<{ projectCount: number; planName: string | null } | null>(null);
  const kbSeededRef = useRef(false);
  // Tracks whether the user has manually picked an audience in Step 2, so
  // switching goals in Step 1 doesn't clobber a deliberate choice.
  const audienceTouchedRef = useRef(false);

  const openDialog = useCallback(() => {
    setState(FRESH);
    setChecks({});
    kbSeededRef.current = false;
    audienceTouchedRef.current = false;
    setCreating(false);
    setPlanInfo(null);
    setOpen(true);

    // Seed the budget field from the tenant's own project history rather
    // than a fixed guess — left blank if there's no data to average.
    listProjects().then((projects) => {
      const budgets = (projects as any[])
        .map((p) => (typeof p.budget === "number" ? p.budget : null))
        .filter((n): n is number => n != null && n > 0);
      if (budgets.length === 0) return;
      const avg = Math.round(budgets.reduce((a, b) => a + b, 0) / budgets.length);
      setState((s) => (s.budget ? s : { ...s, budget: `$${avg.toLocaleString()}` }));
    });

    // Real usage for the Step 3 plan note — no fabricated quota, just what's true today.
    Promise.all([getProjectCounts(), getBillingOverview(getTenantId()).catch(() => null)]).then(
      ([counts, billing]) => {
        setPlanInfo({ projectCount: counts.all, planName: billing?.plan_name ?? null });
      }
    );
  }, []);

  // First two indexed tenant sources are pre-selected by default; only runs once per dialog open.
  const handleKbSourcesLoaded = useCallback((sources: KnowledgeSourceSummary[]) => {
    if (kbSeededRef.current) return;
    kbSeededRef.current = true;
    const defaults: Record<string, boolean> = {};
    sources.slice(0, 2).forEach((s) => { defaults[s.kb_id] = true; });
    setChecks((c) => ({ ...defaults, ...c }));
  }, []);

  useEffect(() => {
    window.addEventListener("blyn:open-new-project", openDialog);
    return () => window.removeEventListener("blyn:open-new-project", openDialog);
  }, [openDialog]);

  const handleClose = () => setOpen(false);
  const update = (key: keyof WizardState, value: string) => {
    if (key === "audience") audienceTouchedRef.current = true;
    setState((s) => {
      const next = { ...s, [key]: value };
      // Re-derive the recommended audience when the goal changes, unless
      // the user has already made an explicit audience choice this session.
      if (key === "goal" && !audienceTouchedRef.current) {
        next.audience = GOAL_AUDIENCE_DEFAULTS[value] ?? s.audience;
      }
      return next;
    });
    if (key === "name" && nameError) setNameError(false);
  };
  const updateDate = (value: Date | null) => setState((s) => ({ ...s, date: value }));
  const toggleCheck = (key: string) => setChecks((c) => ({ ...c, [key]: !c[key as keyof typeof c] }));
  const handleNext = () => {
    if (state.step === 1 && !state.name.trim()) { setNameError(true); return; }
    if (state.step < 3) setState((s) => ({ ...s, step: (s.step + 1) as 1 | 2 | 3 }));
  };
  const handleBack = () => { if (state.step > 1) setState((s) => ({ ...s, step: (s.step - 1) as 1 | 2 | 3 })); };

  const handleCreate = async () => {
    if (!state.name.trim()) { appToast.warning("Give your project a name first", "Name required"); return; }
    setCreating(true);
    try {
      const project = await createProject({
        name: state.name.trim(),
        goal: state.goal === "custom" ? state.customGoal.trim() || "custom" : state.goal,
        audience: state.audience,
        budget: state.budget || undefined,
        launch_date: state.date ? state.date.toISOString() : undefined,
        kb_source_ids: Object.keys(checks).filter((k) => checks[k as keyof typeof checks]),
      });
      if (project === null) return;
      handleClose();
      appToast.success(`${state.name} · ready to go`, "Project created");
      const projectId = (project as any)?.id;
      if (projectId) {
        store.set({
          currentProjectId: projectId,
          currentProjectName: state.name.trim(),
          projectsVersion: ((store.get('projectsVersion') as number) || 0) + 1,
        });
      }
      setTimeout(() => {
        router.push("/blyn/new-conversation");
      }, 150);
    } catch (err) {
      console.error("[NewProject] createProject failed:", err);
    } finally {
      setCreating(false);
    }
  };

  const STEP_LABELS = ["Goal & Name", "Audience & Budget", "Knowledge & Team"];

  return (
    <NewProjectContext.Provider value={openDialog}>
      {children}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
        PaperProps={{ sx: { backgroundImage: "none" } }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="flex-start" gap={1.5}>
            <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: (t) => alpha(t.palette.primary.main, 0.1), color: "primary.main", display: "grid", placeItems: "center", flexShrink: 0 }}>
              <Iconify icon={UI_ICONS.folderLinear} width={20} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ lineHeight: 1.3 }}>New Project</Typography>
              <Typography variant="caption" sx={{ color: "var(--text-tertiary)" }}>Step {state.step} of 3 · {STEP_LABELS[state.step - 1]}</Typography>
            </Box>
            <IconButton size="small" onClick={handleClose} sx={{ mt: -0.5, mr: -0.5 }}>
              <Iconify icon={UI_ICONS.closeLine} width={18} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: '20px !important' }}>
          <Stack direction="row" gap={0.75} sx={{ mb: 2.5 }}>
            {[1, 2, 3].map((n) => (
              <Box key={n} sx={{ flex: 1, height: 3, borderRadius: 2, bgcolor: n <= state.step ? "primary.main" : "divider", opacity: n === state.step ? 0.7 : 1, transition: "background-color 0.2s" }} />
            ))}
          </Stack>
          <Box sx={{ maxWidth: 420, mx: "auto" }}>
            {state.step === 1 && <Step1 state={state} update={update} nameError={nameError} />}
            {state.step === 2 && <Step2 state={state} update={update} updateDate={updateDate} />}
            {state.step === 3 && <Step3 checks={checks} toggle={toggleCheck} onSourcesLoaded={handleKbSourcesLoaded} planInfo={planInfo} />}
          </Box>
        </DialogContent>

        <DialogActions>
          {state.step > 1
            ? <Button variant="outlined" onClick={handleBack} startIcon={<Iconify icon={UI_ICONS.arrowLeft} width={15} />}>Back</Button>
            : <Button variant="outlined" onClick={handleClose}>Cancel</Button>
          }
          <Box sx={{ flex: 1 }} />
          {state.step < 3
            ? <Button onClick={handleNext} endIcon={<Iconify icon={UI_ICONS.arrowRight} width={15} />}>Next</Button>
            : <Button onClick={handleCreate} disabled={creating} startIcon={<Iconify icon={creating ? "svg-spinners:ring-resize" : "solar:check-read-linear"} width={15} />}>Create Project</Button>
          }
        </DialogActions>
      </Dialog>
    </NewProjectContext.Provider>
  );
}
